// src/screens/HowToPlayScreen.js
// ============================================================================
// COMPREHENSIVE GUIDE - How to Play & Stream on CritCash
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated,
  Platform,
  Linking,
  Image,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import * as Clipboard from 'expo-clipboard';

// Icon imports - using @expo/vector-icons (built into Expo)
import { Ionicons, MaterialCommunityIcons, FontAwesome5, Feather } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import useMembershipsLive from '../hooks/useMembershipsLive';

const CAPTURE_CARD_IMAGE = require('../../assets/videoCaptureCard.webp');

// "Free Entry & Plans" support line. Naming memberships is an availability
// claim, so it switches on useMembershipsLive().live (never everLaunched):
// a paused launch goes back to the Super Crits and Rumbles line.
const SUPPORT_LINE_DARK = "Viewers can back the streamers they watch with Super Crits and Rumbles: a Super Crit or stream Rumble supports the verified streamer it's sent to";
const SUPPORT_LINE_LIVE = "Viewers can back the streamers they watch with Super Crits and Rumbles, and join a creator's channel with a monthly membership where the creator offers one: a Super Crit, a stream Rumble or a membership supports the verified streamer it's for";
// Team memberships (evergreen 3.5b, design 25.8 and 13.12): the team kind is
// an availability claim too, so it switches on useMembershipsLive().teamLive.
// Money-free and with no support claim for a team membership (only the channel
// kind supports "the verified streamer it's for").
const SUPPORT_LINE_CHANNEL_AND_TEAM_LIVE = "Viewers can back the streamers they watch with Super Crits and Rumbles, join a creator's channel with a monthly membership where the creator offers one, and join a team's monthly membership where the team offers one: a Super Crit, a stream Rumble or a channel membership supports the verified streamer it's for";
const SUPPORT_LINE_TEAM_LIVE = "Viewers can back the streamers they watch with Super Crits and Rumbles, and join a team's monthly membership where the team offers one: a Super Crit or stream Rumble supports the verified streamer it's sent to";
// Four states on (live, teamLive): (false, false) and (true, false) are the
// 3.5a lines unchanged.
export function supportLineFor(live, teamLive = false) {
  if (teamLive) return live ? SUPPORT_LINE_CHANNEL_AND_TEAM_LIVE : SUPPORT_LINE_TEAM_LIVE;
  return live ? SUPPORT_LINE_LIVE : SUPPORT_LINE_DARK;
}

// ============================================================================
// ICON HELPER - Maps icon names to components
// ============================================================================
const getIcon = (name, size = 20, color = '#FCD000') => {
  const icons = {
    'key': <Ionicons name="key" size={size} color={color} />,
    'videocam': <Ionicons name="videocam" size={size} color={color} />,
    'desktop': <Ionicons name="desktop" size={size} color={color} />,
    'link': <Ionicons name="link" size={size} color={color} />,
    'help': <Ionicons name="help-circle" size={size} color={color} />,
    'warning': <Ionicons name="warning" size={size} color={color} />,
    'checkmark': <Ionicons name="checkmark-circle" size={size} color={color} />,
    'close': <Ionicons name="close-circle" size={size} color={color} />,
    'info': <Ionicons name="information-circle" size={size} color={color} />,
    'flash': <Ionicons name="flash" size={size} color={color} />,
    'game-controller': <Ionicons name="game-controller" size={size} color={color} />,
    'wifi': <Ionicons name="wifi" size={size} color={color} />,
    'trophy': <Ionicons name="trophy" size={size} color={color} />,
    'people': <Ionicons name="people" size={size} color={color} />,
    'target': <MaterialCommunityIcons name="target" size={size} color={color} />,
    'sword-cross': <MaterialCommunityIcons name="sword-cross" size={size} color={color} />,
    'broadcast': <MaterialCommunityIcons name="broadcast" size={size} color={color} />,
    'twitch': <FontAwesome5 name="twitch" size={size} color={color} />,
  };
  return icons[name] || <Ionicons name="ellipse" size={size} color={color} />;
};

// ============================================================================
// COLLAPSIBLE SECTION COMPONENT
// ============================================================================
const CollapsibleSection = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const animatedHeight = useRef(new Animated.Value(defaultOpen ? 1 : 0)).current;

  const toggleSection = () => {
    Animated.timing(animatedHeight, {
      toValue: isOpen ? 0 : 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
    setIsOpen(!isOpen);
  };

  return (
    <View style={styles.collapsibleContainer}>
      <TouchableOpacity style={styles.collapsibleHeader} onPress={toggleSection}>
        <View style={styles.collapsibleTitleRow}>
          <View style={styles.collapsibleIcon}>{icon}</View>
          <Text style={styles.collapsibleTitle}>{title}</Text>
        </View>
        <Ionicons 
          name={isOpen ? "chevron-down" : "chevron-forward"} 
          size={20} 
          color="#FCD000" 
        />
      </TouchableOpacity>
      
      {isOpen && (
        <View style={styles.collapsibleContent}>
          {children}
        </View>
      )}
    </View>
  );
};

// ============================================================================
// COPYABLE CODE BLOCK COMPONENT
// ============================================================================
const CodeBlock = ({ label, value, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await Clipboard.setStringAsync(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onCopy) onCopy();
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <View style={styles.codeBlockContainer}>
      {label && <Text style={styles.codeBlockLabel}>{label}</Text>}
      <TouchableOpacity style={styles.codeBlock} onPress={handleCopy}>
        <Text style={styles.codeBlockText} numberOfLines={1}>{value}</Text>
        {copied 
          ? <Ionicons name="checkmark" size={18} color="#2ECC71" style={{marginLeft: 8}} />
          : <Ionicons name="copy-outline" size={18} color="#888" style={{marginLeft: 8}} />
        }
      </TouchableOpacity>
    </View>
  );
};

// ============================================================================
// STEP COMPONENT
// ============================================================================
const Step = ({ number, title, children }) => (
  <View style={styles.stepContainer}>
    <View style={styles.stepHeader}>
      <View style={styles.stepNumber}>
        <Text style={styles.stepNumberText}>{number}</Text>
      </View>
      <Text style={styles.stepTitle}>{title}</Text>
    </View>
    <View style={styles.stepContent}>
      {children}
    </View>
  </View>
);

// ============================================================================
// TIP BOX COMPONENT
// ============================================================================
const TipBox = ({ type = 'info', children }) => {
  const config = {
    info: { icon: 'info', bg: '#1a2a3a', border: '#3498db', color: '#3498db' },
    warning: { icon: 'warning', bg: '#3a2a1a', border: '#f39c12', color: '#f39c12' },
    success: { icon: 'checkmark', bg: '#1a3a2a', border: '#2ecc71', color: '#2ecc71' },
    error: { icon: 'close', bg: '#3a1a1a', border: '#e74c3c', color: '#e74c3c' },
  };
  const { icon, bg, border, color } = config[type];

  return (
    <View style={[styles.tipBox, { backgroundColor: bg, borderLeftColor: border }]}>
      <View style={styles.tipIcon}>{getIcon(icon, 18, color)}</View>
      <Text style={styles.tipText}>{children}</Text>
    </View>
  );
};

// ============================================================================
// LIST ITEM COMPONENT (with proper bullet)
// ============================================================================
const ListItem = ({ children, icon = 'bullet' }) => (
  <View style={styles.listItemRow}>
    {icon === 'bullet' ? (
      <Text style={styles.bullet}>-</Text>
    ) : icon === 'check' ? (
      <Ionicons name="checkmark" size={14} color="#2ECC71" style={styles.listIcon} />
    ) : (
      <Ionicons name="ellipse" size={6} color="#FCD000" style={styles.listIcon} />
    )}
    <Text style={styles.listItemText}>{children}</Text>
  </View>
);

// ============================================================================
// MAIN SCREEN
// ============================================================================
export default function HowToPlayScreen({ navigation }) {
  const [activeTab, setActiveTab] = useState('play'); // 'play' | 'stream' | 'host'
  const { live: membershipsLive, teamLive: teamMembershipsLive } = useMembershipsLive();
  // Simulcast section is gated on the live product flag (settings/restream is
  // world-readable) — this page must not promise a perk that is still dark.
  // Until it flips, streamers see the free-tier "second output in OBS" advice.
  const [simulcastLive, setSimulcastLive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(), 'settings', 'restream'));
        if (!cancelled) setSimulcastLive(snap.exists() && snap.data()?.enabled === true);
      } catch {
        if (!cancelled) setSimulcastLive(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scrollRef = useRef(null);

  const tabs = [
    { id: 'play', label: 'Play', icon: 'game-controller', title: 'How Events Work' },
    { id: 'stream', label: 'Stream', icon: 'broadcast', title: 'Streaming Setup' },
    { id: 'host', label: 'Host', icon: 'trophy', title: 'Become a Host' },
  ];

  return (
    <View style={styles.outerContainer}>
      <Header navigation={navigation} />
      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.tabActive]}
            onPress={() => setActiveTab(tab.id)}
          >
            <View style={styles.tabContent}>
              {getIcon(tab.icon, 16, activeTab === tab.id ? '#FCD000' : '#666')}
              <Text style={[styles.tabText, activeTab === tab.id && styles.tabTextActive]}>
                {tab.label}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView 
        ref={scrollRef}
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <Text style={styles.title}>
          {tabs.find(t => t.id === activeTab)?.title}
        </Text>

        {/* ================================================================ */}
        {/* TAB: HOW TO PLAY */}
        {/* ================================================================ */}
        {activeTab === 'play' && (
          <>
            <Text style={styles.subtitle}>
              Events are one of the tools built into CritCash: join one in the games you
              already play, or run your own for your community. Every event is free to enter, and every result goes on your record.
            </Text>

            <Step number="1" title="Get Set Up to Compete">
              <Text style={styles.bodyText}>
                A quick setup keeps matches running smoothly and fairly for everyone.
              </Text>
              <ListItem>Confirm your email address</ListItem>
              <ListItem>Verify your phone number</ListItem>
              <ListItem>Set up your streaming account (required to compete)</ListItem>
              <ListItem>If you play on a console: connect a video capture card to your PC or laptop - see the Stream tab for setup</ListItem>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => navigation.navigate('Verification')}
              >
                <Text style={styles.actionButtonText}>Go to Verification Center</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{marginLeft: 6}} />
              </TouchableOpacity>
            </Step>

            <Step number="2" title="Free Entry & Plans">
              <Text style={styles.bodyText}>
                Every event on CritCash is free to enter. A paid plan is optional.
                Every plan, free included, keeps your stream recordings as Videos
                for its retention period. Some paid plans keep them longer, and
                paid plans unlock extras:
              </Text>
              <ListItem>Entry is free — no purchase required to compete</ListItem>
              <ListItem>Paid plans unlock perks, and some keep your Videos longer</ListItem>
              <ListItem>{supportLineFor(membershipsLive, teamMembershipsLive)}</ListItem>
              <ListItem>Sent to a squad, either one is shared equally among the verified squad members streaming when it's sent; an event Rumble is shared equally among the verified players who played, whatever their placement, once results are final, and is refunded if the event is cancelled, isn't completed, or no one in it can receive a share</ListItem>

              <TipBox type="info">
                Entry is free on every event, and your wins and placements
                go on your record.
              </TipBox>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => navigation.navigate('Subscription')}
              >
                <Text style={styles.actionButtonText}>Plans & Videos</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{marginLeft: 6}} />
              </TouchableOpacity>
            </Step>

            <Step number="3" title="Find Your Competition">
              <Text style={styles.bodyText}>
                Browse the match lobby to find the perfect competition for you:
              </Text>

              <View style={styles.formatCard}>
                <View style={styles.formatHeader}>
                  {getIcon('sword-cross', 18, '#FCD000')}
                  <Text style={styles.formatTitle}>Head-to-Head</Text>
                </View>
                <Text style={styles.formatDesc}>
                  Go one-on-one in games like Madden or Gran Turismo 7.
                </Text>
              </View>

              <View style={styles.formatCard}>
                <View style={styles.formatHeader}>
                  {getIcon('people', 18, '#FCD000')}
                  <Text style={styles.formatTitle}>Team-Based</Text>
                </View>
                <Text style={styles.formatDesc}>
                  Squad up with friends in Call of Duty or Marvel Rivals.
                </Text>
              </View>

              <View style={styles.formatCard}>
                <View style={styles.formatHeader}>
                  {getIcon('trophy', 18, '#FCD000')}
                  <Text style={styles.formatTitle}>Official Bracket Competitions</Text>
                </View>
                <Text style={styles.formatDesc}>
                  Large-scale bracket competitions organized by CritCash — the biggest fields and the biggest stage.
                </Text>
              </View>

              <View style={styles.formatCard}>
                <View style={styles.formatHeader}>
                  {getIcon('target', 18, '#FCD000')}
                  <Text style={styles.formatTitle}>User-Created Matches</Text>
                </View>
                <Text style={styles.formatDesc}>
                  Challenge your friends or rivals in custom matches you create.
                </Text>
              </View>
            </Step>

            <Step number="4" title="Enter & Compete">
              <Text style={styles.bodyText}>
                Every event is free to enter. Claim your spot, be streaming and
                ready when your match is called, and play. If an event doesn't
                reach capacity, brackets may be adjusted before it starts.
              </Text>

              <View style={styles.poolInfoBox}>
                <Text style={styles.poolInfoTitle}>How Results Work</Text>
                <Text style={styles.poolInfoText}>
                  On supported games, matches are Verified straight from your stream; on other games, players report the result. When the
                  event ends, placements are final and go on your record —
                  leaderboards and your profile. Brackets are locked once the
                  event starts.
                </Text>
              </View>

              <TipBox type="success">
                Every result counts. Wins and placements build the record that
                follows you.
              </TipBox>
            </Step>

            <Step number="5" title="Stream Your Matches (Required)">
              <View style={styles.requiredBadge}>
                <Ionicons name="alert-circle" size={16} color="#E74C3C" />
                <Text style={styles.requiredBadgeText}>STREAMING IS REQUIRED TO COMPETE</Text>
              </View>

              <Text style={styles.bodyText}>
                Download CritCast for Windows to sign in and go live (it sets up OBS for you),
                or stream your gameplay through OBS Studio, Streamlabs, or similar streaming
                software. Streaming is required for every competitive match - it's how our
                platform verifies results on supported games, settles disputes, and keeps play fair.
              </Text>

              <View style={styles.whyStreamBox}>
                <Text style={styles.whyStreamTitle}>Why Streaming is Required</Text>
                <ListItem icon="check">On supported games, your stream verifies the result automatically</ListItem>
                <ListItem icon="check">Streams provide evidence for dispute resolution if needed</ListItem>
                <ListItem icon="check">Protects all players against cheating and fraudulent results</ListItem>
                <ListItem icon="check">Creates a transparent, verifiable record of every match</ListItem>
              </View>

              <TipBox type="warning">
                You cannot join or compete in events without an active stream to
                CritCash. Make sure your streaming setup is configured before entering.
              </TipBox>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => setActiveTab('stream')}
              >
                <Text style={styles.actionButtonText}>View Streaming Setup Guide</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{marginLeft: 6}} />
              </TouchableOpacity>
            </Step>

            <Step number="6" title="Create Your Own — and Fill the Grid">
              <Text style={styles.bodyText}>
                Anyone can create events and leagues from the lobby. Fill every
                seat of a full-size event with real, verified players — and
                finish it — and it certifies as a
                <Text style={styles.highlight}> Verified Sellout</Text> about
                48 hours after it wraps.
              </Text>
              <ListItem icon="check">Full Crown — certified sellouts go on your profile permanently, with a running count that never resets</ListItem>
              <ListItem icon="check">Run It Back — one tap reruns a certified event for the same crowd, a week out at the same time, and everyone who showed up ready gets the call</ListItem>
              <ListItem icon="check">Leagues run it back as a next season, roster invited back</ListItem>

              <TipBox type="info">
                Small casual grids don't qualify, and the roughly-48-hour review
                is normal — that's the certification settling.
              </TipBox>
            </Step>

            {/* About competing */}
            <View style={styles.disclaimerBox}>
              <Text style={styles.disclaimerTitle}>About Competing</Text>
              <Text style={styles.disclaimerText}>
                Entry to every event is free. Standings are decided by skill,
                and your record stays
                with you. CritCash is open to players 18 and older.
              </Text>
            </View>
          </>
        )}

        {/* ================================================================ */}
        {/* TAB: STREAMING SETUP */}
        {/* ================================================================ */}
        {activeTab === 'stream' && (
          <>
            <Text style={styles.subtitle}>
              Download CritCast for Windows: sign in and go live. It sets up OBS for you.
              You can also use your single stream key in OBS Studio or Streamlabs for every game you play; the same setup streams your matches when you join an event.
            </Text>

            {/* Requirement Notice */}
            <View style={styles.requirementNotice}>
              <Ionicons name="information-circle" size={24} color="#3498db" />
              <View style={styles.requirementNoticeContent}>
                <Text style={styles.requirementNoticeTitle}>Streaming in Events</Text>
                <Text style={styles.requirementNoticeText}>
                  Download CritCast for Windows (it sets up OBS for you) or stream through OBS, Streamlabs, or similar software to take part
                  in any event. On supported games, our systems use your stream to verify results; on other games, players report results and our team reviews any dispute.
                  {'\n\n'}
                  <Text style={styles.bold}>Playing on PS5, Xbox, or Switch?</Text> Connect a video capture
                  card to your PC or laptop. See the first section below.
                </Text>
              </View>
            </View>

            {/* Quick Start */}
            <View style={styles.quickStartBox}>
              <View style={styles.quickStartHeader}>
                <Ionicons name="flash" size={20} color="#FCD000" />
                <Text style={styles.quickStartTitle}>Quick Start Checklist</Text>
              </View>
              <View style={styles.checklistItem}>
                <Ionicons name="square-outline" size={16} color="#888" />
                <Text style={styles.checklistText}>Console players: connect a video capture card to your PC or laptop</Text>
              </View>
              <View style={styles.checklistItem}>
                <Ionicons name="square-outline" size={16} color="#888" />
                <Text style={styles.checklistText}>Download CritCast for Windows (sets up OBS automatically), or get your stream key and configure OBS or Streamlabs</Text>
              </View>
              <View style={styles.checklistItem}>
                <Ionicons name="square-outline" size={16} color="#888" />
                <Text style={styles.checklistText}>Test your stream connection</Text>
              </View>
            </View>

            {/* Section 0: Streaming from a Console */}
            <CollapsibleSection
              title="Streaming from a Console? (PS5 / Xbox / Switch)"
              icon={getIcon('game-controller', 18, '#FCD000')}
              defaultOpen={true}
            >
              <Text style={styles.bodyText}>
                Consoles can't stream straight to CritCast: their built-in broadcast apps only
                go to Twitch and YouTube. Connect a <Text style={styles.highlight}>video capture card</Text> between
                your console and your PC or laptop, and OBS (or CritCast for Windows) reads your
                gameplay from it.
              </Text>

              <Text style={styles.bodyText}>
                <Text style={styles.bold}>A video capture card works with every console.</Text> Your
                gameplay passes through to your TV with no added lag, and OBS reads it from
                the card.
              </Text>

              <View style={styles.captureCardImageWrapper}>
                <Image
                  source={CAPTURE_CARD_IMAGE}
                  style={styles.captureCardImage}
                  resizeMode="contain"
                />
                <Text style={styles.captureCardCaption}>
                  A typical USB video capture card
                </Text>
              </View>

              <View style={styles.poolInfoBox}>
                <Text style={styles.poolInfoTitle}>What You Need</Text>
                <Text style={styles.poolInfoText}>
                  Video capture card: ~$16 and up on Amazon, Walmart, or Best Buy{'\n'}
                  An HDMI cable (most capture cards include one){'\n'}
                  A PC or laptop running OBS Studio or Streamlabs
                </Text>
              </View>

              <Step number="1" title="Plug In the Capture Card">
                <Text style={styles.bodyText}>
                  Run an HDMI cable from your console's HDMI OUT into the capture card's
                  HDMI IN. Then run a second HDMI cable from the capture card's HDMI OUT
                  to your TV or monitor so you can still see your game normally.
                </Text>
              </Step>

              <Step number="2" title="Connect to Your PC">
                <Text style={styles.bodyText}>
                  Plug the capture card's USB cable into your PC or laptop. Windows or macOS
                  will detect it automatically - no driver install needed for most budget
                  models.
                </Text>
              </Step>

              <Step number="3" title="Add It as a Source in OBS">
                <Text style={styles.bodyText}>
                  In OBS or Streamlabs, click the <Text style={styles.highlight}>+</Text> button
                  under Sources and choose <Text style={styles.highlight}>Video Capture Device</Text>.
                  Select your capture card from the dropdown - your console's gameplay should
                  appear in the OBS preview window.
                </Text>
              </Step>

              <TipBox type="info">
                Once your gameplay shows up in OBS, finish the OBS Studio setup below to point
                your stream at CritCash. You'll only need to set the capture card up once.
              </TipBox>

              <TipBox type="info">
                The Twitch and YouTube apps built into PS5 and Xbox can't send your stream
                to CritCash on their own - they don't support our stream server. But you
                <Text style={styles.bold}> can run them in parallel</Text>: send your CritCash
                stream through OBS while your console broadcasts to Twitch or YouTube natively.
                That uses double the upload bandwidth, but it lets you keep your existing
                audience while you stream on CritCash.
              </TipBox>
            </CollapsibleSection>

            {/* Section 1: Get Your Stream Key */}
            <CollapsibleSection
              title="Get Your CritCash Stream Key"
              icon={getIcon('key', 18, '#FCD000')}
            >
              <Text style={styles.bodyText}>
                Your stream key is your unique identifier for broadcasting to CritCash.
                Keep it private - anyone with your key can stream to your account.
              </Text>

              <Step number="1" title="Navigate to Streaming Hub">
                <Text style={styles.bodyText}>
                  Go to <Text style={styles.highlight}>Profile - Streaming Accounts</Text> or 
                  tap the button below:
                </Text>
                <TouchableOpacity 
                  style={styles.actionButton}
                  onPress={() => navigation.navigate('StreamingAccounts')}
                >
                  <Text style={styles.actionButtonText}>Open Streaming Hub</Text>
                  <Ionicons name="arrow-forward" size={16} color="#000" style={{marginLeft: 6}} />
                </TouchableOpacity>
              </Step>

              <Step number="2" title="Generate Your Key">
                <Text style={styles.bodyText}>
                  Click "Generate Stream Key" if you don't have one yet. You'll receive:
                </Text>
                <ListItem><Text style={styles.bold}>Server URL:</Text> rtmp://stream.critcash.com/live</ListItem>
                <ListItem><Text style={styles.bold}>Stream Key:</Text> sk_live_xxxxxxxx (your unique key)</ListItem>
              </Step>

              <Step number="3" title="Copy Your Credentials">
                <Text style={styles.bodyText}>
                  Click the copy icon next to each field to copy them to your clipboard.
                  You'll need both for the next step.
                </Text>
              </Step>

              <TipBox type="warning">
                Never share your stream key publicly! If compromised, regenerate it
                immediately from the Streaming Hub.
              </TipBox>
            </CollapsibleSection>

            {/* Section 2: OBS Setup */}
            <CollapsibleSection 
              title="OBS Studio Setup" 
              icon={getIcon('videocam', 18, '#FCD000')}
            >
              <Text style={styles.bodyText}>
                OBS Studio is free, open-source software for streaming. Here's how to
                configure it for CritCash:
              </Text>

              <Step number="1" title="Download OBS">
                <Text style={styles.bodyText}>
                  If you don't have OBS, download it from the official website:
                </Text>
                <TouchableOpacity 
                  style={styles.linkButton}
                  onPress={() => Linking.openURL('https://obsproject.com')}
                >
                  <Ionicons name="globe-outline" size={16} color="#3498db" />
                  <Text style={styles.linkButtonText}>obsproject.com</Text>
                </TouchableOpacity>
              </Step>

              <Step number="2" title="Open Settings">
                <Text style={styles.bodyText}>
                  In OBS, go to <Text style={styles.highlight}>Settings - Stream</Text>
                </Text>
              </Step>

              <Step number="3" title="Configure Stream Settings">
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Service:</Text>
                  <Text style={styles.settingValue}>Custom...</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Server:</Text>
                  <Text style={styles.settingValue}>rtmp://stream.critcash.com/live</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Stream Key:</Text>
                  <Text style={styles.settingValue}>(paste your key from Streaming Hub)</Text>
                </View>
              </Step>

              <Step number="4" title="Configure Output Settings">
                <Text style={styles.bodyText}>
                  Go to <Text style={styles.highlight}>Settings - Output</Text> and set:
                </Text>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Output Mode:</Text>
                  <Text style={styles.settingValue}>Advanced</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Encoder:</Text>
                  <Text style={styles.settingValue}>x264 or NVENC (if available)</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Rate Control:</Text>
                  <Text style={styles.settingValue}>CBR</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Bitrate:</Text>
                  <Text style={styles.settingValue}>4500-6000 Kbps</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Keyframe Interval:</Text>
                  <Text style={styles.settingValue}>2</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Profile:</Text>
                  <Text style={styles.settingValue}>high</Text>
                </View>

                <TipBox type="info">
                  Set B-frames to 0 if you experience playback issues. This is found
                  under the encoder settings.
                </TipBox>
              </Step>

              <Step number="5" title="Video Settings">
                <Text style={styles.bodyText}>
                  Go to <Text style={styles.highlight}>Settings - Video</Text>:
                </Text>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Base Resolution:</Text>
                  <Text style={styles.settingValue}>1920x1080</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Output Resolution:</Text>
                  <Text style={styles.settingValue}>1920x1080</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>FPS:</Text>
                  <Text style={styles.settingValue}>30 or 60</Text>
                </View>
              </Step>

              <Step number="6" title="Start Streaming">
                <Text style={styles.bodyText}>
                  Click "Start Streaming" in OBS. Your stream should appear in
                  CritCash within a few seconds.
                </Text>
              </Step>
            </CollapsibleSection>

            {/* Section 3: Streamlabs Setup */}
            <CollapsibleSection 
              title="Streamlabs Desktop Setup" 
              icon={getIcon('desktop', 18, '#FCD000')}
            >
              <Text style={styles.bodyText}>
                Streamlabs Desktop is a popular alternative to OBS with additional
                features. Configuration is similar:
              </Text>

              <Step number="1" title="Open Settings">
                <Text style={styles.bodyText}>
                  Click the gear icon then select <Text style={styles.highlight}>Stream</Text>
                </Text>
              </Step>

              <Step number="2" title="Configure Stream">
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Stream Type:</Text>
                  <Text style={styles.settingValue}>Custom Streaming Server</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>URL:</Text>
                  <Text style={styles.settingValue}>rtmp://stream.critcash.com/live</Text>
                </View>
                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>Stream Key:</Text>
                  <Text style={styles.settingValue}>(paste from Streaming Hub)</Text>
                </View>
              </Step>

              <Step number="3" title="Output Settings">
                <Text style={styles.bodyText}>
                  Navigate to <Text style={styles.highlight}>Output</Text> and match
                  the OBS settings above (CBR, 4500-6000 Kbps, etc.)
                </Text>
              </Step>
            </CollapsibleSection>

            {/* Section 4: Simulcast to Other Platforms */}
            <CollapsibleSection
              title={simulcastLive
                ? 'Simulcast to Twitch, YouTube & Kick (Included with Paid Plans)'
                : 'Streaming to Twitch or YouTube Too? (Optional)'}
              icon={getIcon('wifi', 18, '#FCD000')}
            >
              {simulcastLive ? (
                <Text style={styles.bodyText}>
                  You only ever stream to CritCash. Paid plans include
                  Simulcast: we automatically rebroadcast your live stream to
                  Twitch, YouTube, Kick and more at the same time — one upload
                  from your PC, no watermark, no extra software. Set your
                  destinations up once in <Text style={styles.highlight}>Profile → Streaming Accounts → Simulcast</Text>.
                  On the free tier you can still add a second output in OBS to
                  reach another platform yourself.
                </Text>
              ) : (
                <Text style={styles.bodyText}>
                  You only need to stream to CritCash. If you also
                  want to reach your audience on Twitch, Kick or YouTube at the
                  same time, add a second output in OBS. Keep your canvas
                  horizontal (16:9) either way. On supported games, we read the game from your
                  stream to confirm results, and a vertical layout breaks
                  that read.
                </Text>
              )}
            </CollapsibleSection>

            {/* Section 5: Troubleshooting */}
            <CollapsibleSection
              title="Troubleshooting"
              icon={getIcon('help', 18, '#FCD000')}
            >
              <Text style={[styles.bodyText, { marginBottom: 14 }]}>
                Hit a snag? Most issues fall into one of these buckets. If
                nothing here matches, hop into our Discord community for
                live help.
              </Text>

              {/* ---------- CONSOLE / CAPTURE CARD ---------- */}
              <Text style={styles.troubleshootCategory}>Console &amp; Capture Card (PS5 / Xbox / Switch)</Text>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Capture card shows a black screen?</Text>
                <Text style={styles.troubleshootA}>
                  - <Text style={styles.bold}>PS5 most common cause:</Text> HDCP is on. Disable it: Settings - System - HDMI - turn OFF "Enable HDCP". Restart the PS5.{'\n'}
                  - Xbox: Settings - General - TV &amp; Display Options - Video Modes - turn OFF "Allow HDR10". Try again.{'\n'}
                  - Verify the HDMI cable from console goes into the capture card's HDMI IN (not OUT).{'\n'}
                  - Try a different HDMI cable — cheap or older cables can drop the signal.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Capture card not showing up in OBS sources?</Text>
                <Text style={styles.troubleshootA}>
                  - Unplug the USB cable, plug it back in, give Windows / macOS 10 seconds to detect it.{'\n'}
                  - Plug directly into a USB port on the PC — USB hubs and front-panel ports often don't have the bandwidth.{'\n'}
                  - In OBS: click + under Sources - "Video Capture Device" - dropdown should list the card. If it's not there, close OBS, reconnect the card, reopen OBS.{'\n'}
                  - Driver-required cards (Elgato HD60 X, etc.) need their software installed first. Budget cards usually work driverless.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>No game audio in the stream?</Text>
                <Text style={styles.troubleshootA}>
                  - In OBS, click the gear icon on the Video Capture Device - "Properties for Audio Output Mode" - select "Output desktop audio (DirectSound)" or similar.{'\n'}
                  - Make sure your console isn't routing audio over Bluetooth headphones (that bypasses the HDMI audio the capture card sees). Use the console's TV speakers or wired headphones.{'\n'}
                  - PS5: Settings - Sound - Audio Output - confirm HDMI is selected, not the controller jack.
                </Text>
              </View>

              {/* ---------- OBS CONNECTION ---------- */}
              <Text style={styles.troubleshootCategory}>OBS / Streamlabs Connection</Text>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>"Failed to connect to server" when starting stream?</Text>
                <Text style={styles.troubleshootA}>
                  - Double-check the server URL is exactly <Text style={styles.highlight}>rtmp://stream.critcash.com/live</Text> — easy to mistype.{'\n'}
                  - Re-paste the stream key from the Streaming Hub (no leading/trailing spaces).{'\n'}
                  - Some workplace / school / hotel networks block RTMP port 1935. Try a phone hotspot to test.{'\n'}
                  - Restart OBS — sometimes its connection cache gets stuck.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Getting "Stream Key Invalid" errors?</Text>
                <Text style={styles.troubleshootA}>
                  - Regenerate your stream key in Profile - Streaming Accounts.{'\n'}
                  - Paste fresh into OBS Stream settings; watch for extra spaces.{'\n'}
                  - If you have OBS open in multiple instances, only one can use the key at a time.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Stream not appearing on CritCash even though OBS shows LIVE?</Text>
                <Text style={styles.troubleshootA}>
                  - Wait 10-15 seconds — there's a small ingest delay.{'\n'}
                  - Refresh the CritCash page or close + reopen the event view.{'\n'}
                  - Check OBS bottom-right for "Connection: Stable" — if it's red, the stream isn't actually reaching the server.
                </Text>
              </View>

              {/* ---------- STREAM QUALITY ---------- */}
              <Text style={styles.troubleshootCategory}>Stream Quality / Performance</Text>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>"Encoder overload" warning / CPU pegged at 100%?</Text>
                <Text style={styles.troubleshootA}>
                  - Switch encoder: Settings - Output - Encoder - try NVENC (NVIDIA GPU) or QuickSync (Intel) instead of x264. Massive CPU savings.{'\n'}
                  - Drop output resolution to 1280x720 in Settings - Video.{'\n'}
                  - Change x264 preset from "medium" to "veryfast" or "superfast".{'\n'}
                  - Close other resource-heavy apps (browsers with many tabs, game launchers).
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Stream is buffering / choppy / dropping frames?</Text>
                <Text style={styles.troubleshootA}>
                  - Lower your bitrate: Settings - Output - try 3500 Kbps. Re-test.{'\n'}
                  - Use a wired ethernet connection — Wi-Fi (especially 2.4 GHz) is the #1 cause of dropped frames.{'\n'}
                  - Check upload speed at fast.com — you need at least 1.5x your bitrate (so 5 Mbps upload to safely stream 3500 Kbps).{'\n'}
                  - Close anything pulling bandwidth: cloud sync, Steam downloads, household streaming.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Stream looks pixelated or low quality?</Text>
                <Text style={styles.troubleshootA}>
                  - Settings - Video - make sure Base and Output resolutions match (both 1920x1080 OR both 1280x720).{'\n'}
                  - In Settings - Output set Rate Control to CBR and bitrate to at least 4500 Kbps for 1080p.{'\n'}
                  - Set keyframe interval to 2 (Settings - Output - Advanced).
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Stream is delayed several seconds behind real gameplay?</Text>
                <Text style={styles.troubleshootA}>
                  - That's normal — RTMP/RTMPS adds 5-10 seconds of latency. The CritCash event view always shows the delayed stream.{'\n'}
                  - For your own gameplay reference, watch your TV / monitor directly (the capture card passthrough is lag-free), NOT the stream.
                </Text>
              </View>

              {/* ---------- MATCH VERIFICATION ---------- */}
              <Text style={styles.troubleshootCategory}>Match Verification</Text>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>I won my race but the system didn't record it?</Text>
                <Text style={styles.troubleshootA}>
                  - Make sure the final results screen (with your finishing position visible) stays on screen for at least 5 seconds — don't skip past it.{'\n'}
                  - Remove webcam / face-cam overlays that cover the HUD or finishing position UI in OBS.{'\n'}
                  - If the issue persists, take a screenshot of the results screen and report it through Profile - Support; an admin will record the result manually and the dev team will investigate.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>My result was flagged for review?</Text>
                <Text style={styles.troubleshootA}>
                  - This usually means the result screen wasn't clear enough (overlay covering HUD, low quality stream, or partial frame).{'\n'}
                  - The match goes to admin review automatically — no action needed from you. Resolution typically within 24 hours.{'\n'}
                  - For future races, keep the bottom-right HUD area unobstructed by overlays.
                </Text>
              </View>

              <View style={styles.troubleshootItem}>
                <Text style={styles.troubleshootQ}>Need more help?</Text>
                <Text style={styles.troubleshootA}>
                  Hop into our Discord community for live assistance from staff
                  and other competitors, or email support through Profile - Help.
                </Text>
              </View>
            </CollapsibleSection>
          </>
        )}

        {/* ================================================================ */}
        {/* TAB: BECOME A HOST */}
        {/* ================================================================ */}
        {activeTab === 'host' && (
          <>
            <Text style={styles.subtitle}>
              Sign up to host events that need a host and keep the matches
              running. No paid plan needed, no entry fee. Every round you host
              goes on your hosting record. Hosts are never auto-assigned; you
              choose what to host.
            </Text>

            <Step number="1" title="What is a Host?">
              <Text style={styles.bodyText}>
                Hosts are event facilitators who keep matches running on time.
                You create lobbies, invite players, set the correct game parameters,
                and make sure everything runs smoothly.
              </Text>
              <ListItem>Create game lobbies for each match</ListItem>
              <ListItem>Invite the correct players to each lobby</ListItem>
              <ListItem>Set game parameters per event rules</ListItem>
              <ListItem>Start the match when it's time to play</ListItem>

              <TipBox type="info">
                You don't need to referee or review evidence. On supported
                games, verification happens automatically. You just make
                sure the games run on time!
              </TipBox>

              <TipBox type="info">
                Hosting is a role you sign up for.
                1v1 (Head-to-Head) and ordinary solo matches have no host role;
                those players just start their own lobby. A host role exists
                only on events that offer host slots (team / CDL events and GT7
                races). Hosts are never auto-assigned - you choose what to host.
              </TipBox>
            </Step>

            <Step number="2" title="How to Enter as a Host">
              <Text style={styles.bodyText}>
                On events that offer host slots, you'll see two entry options:
                <Text style={styles.highlight}> Enter as Participant</Text> (you compete)
                or <Text style={styles.highlight}> Enter as Host</Text> (free, you facilitate).
                Ordinary 1v1 and solo events won't show a host option.
              </Text>
              <ListItem>Tap "Enter as Host (Free)" on the event page</ListItem>
              <ListItem>Select which rounds you want to host</ListItem>
              <ListItem>Confirm your entry - hosting is always free</ListItem>
              <ListItem>You'll be assigned matches in your selected rounds when the event starts</ListItem>

              <TipBox type="success">
                Hosting is free — no entry cost, no paid plan needed. Every
                round you host goes on your hosting record. A great way to stay
                in the community.
              </TipBox>

              <TipBox type="warning">
                You cannot enter the same event as both a Participant and a Host.
                Choose one role per event.
              </TipBox>
            </Step>

            <Step number="3" title="Your Hosting Record">
              <Text style={styles.bodyText}>
                Sign up to host an event, pick your rounds, and run your matches.
                Every round you host goes on your hosting record. Only sign-ups
                are recorded; hosts are never auto-assigned.
              </Text>

              <View style={styles.poolExplainer}>
                <View style={styles.poolHeader}>
                  <MaterialCommunityIcons name="star-circle" size={18} color="#FCD000" />
                  <Text style={styles.poolTitle}>On Your Record, Every Round</Text>
                </View>
                <Text style={styles.poolDesc}>
                  Every round you host goes on your hosting record.{'\n'}
                  Your Host Quality Score tracks on-time starts, correct settings, and ratings.{'\n'}
                  Only sign-ups are recorded — hosts are never auto-assigned.
                </Text>
              </View>

              <TipBox type="success">
                Hosting keeps you in the game — run events, build your hosting
                record, and stay in the mix on nights you aren't competing.
              </TipBox>
            </Step>

            <Step number="4" title="Quality and Penalties">
              <Text style={styles.bodyText}>
                Hosting is a responsibility. Your ratings and operational metrics
                directly affect your Host Quality Score. Consistent problems will
                lower your score and may result in warnings or suspension.
              </Text>

              <View style={styles.poolExplainer}>
                <View style={styles.poolHeader}>
                  <Ionicons name="trending-up" size={18} color="#2196F3" />
                  <Text style={styles.poolTitle}>Host Quality Score</Text>
                </View>
                <Text style={styles.poolDesc}>
                  Volume (25%): events you've hosted{'\n'}
                  Participant Ratings (40%): Post-match surveys on punctuality, communication, and more{'\n'}
                  Operational Score (35%): On-time starts, correct settings, completion rate
                </Text>
              </View>

              <View style={styles.penaltyTable}>
                <View style={styles.penaltyTableHeader}>
                  <Text style={styles.penaltyTableHeaderText}>Issue</Text>
                  <Text style={styles.penaltyTableHeaderText}>Consequence</Text>
                </View>
                <View style={styles.penaltyTableRow}>
                  <View style={[styles.penaltyTableBadge, { backgroundColor: '#f39c1233' }]}>
                    <Text style={[styles.penaltyTableBadgeText, { color: '#f39c12' }]}>Low Rating</Text>
                  </View>
                  <Text style={styles.penaltyTableText}>Probation + quality warning</Text>
                </View>
                <View style={styles.penaltyTableRow}>
                  <View style={[styles.penaltyTableBadge, { backgroundColor: '#e74c3c33' }]}>
                    <Text style={[styles.penaltyTableBadgeText, { color: '#e74c3c' }]}>Sustained Low</Text>
                  </View>
                  <Text style={styles.penaltyTableText}>Hosting privileges suspended</Text>
                </View>
                <View style={styles.penaltyTableRow}>
                  <View style={[styles.penaltyTableBadge, { backgroundColor: '#2ecc7133' }]}>
                    <Text style={[styles.penaltyTableBadgeText, { color: '#2ecc71' }]}>No-Show</Text>
                  </View>
                  <Text style={styles.penaltyTableText}>Strike issued, warning</Text>
                </View>
                <View style={styles.penaltyTableRow}>
                  <View style={[styles.penaltyTableBadge, { backgroundColor: '#c0392b33' }]}>
                    <Text style={[styles.penaltyTableBadgeText, { color: '#c0392b' }]}>8+ Failures</Text>
                  </View>
                  <Text style={[styles.penaltyTableText, { color: '#c0392b' }]}>Permanent hosting ban</Text>
                </View>
              </View>

              <TipBox type="info">
                If your average rating drops below the threshold, you'll be placed
                on probation first. Improve your ratings within 30 days to avoid
                suspension. The system is designed to be fair and give you time
                to correct course.
              </TipBox>
            </Step>

            <Step number="5" title="Get Started">
              <Text style={styles.bodyText}>
                Ready to start hosting? Visit the Host Program to opt in, then
                enter any event that needs a host!
              </Text>
              <TouchableOpacity 
                style={styles.actionButton}
                onPress={() => navigation.navigate('HostScreen')}
              >
                <Text style={styles.actionButtonText}>Go to Host Program</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{marginLeft: 6}} />
              </TouchableOpacity>
            </Step>
          </>
        )}

        {/* Back Button */}
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => {
            // Check if we can go back in navigation stack
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              // Fallback to Lobby if no previous screen
              navigation.navigate('Lobby');
            }
          }}
        >
          <Ionicons name="arrow-back" size={18} color="#FCD000" />
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>

      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
      <StatusBar style="light" />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: '#000',
  },

  // Tab Navigation
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#111',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    paddingTop: Platform.OS === 'ios' ? 50 : 10,
  },
  tab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#FCD000',
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '600',
  },
  tabTextActive: {
    color: '#FCD000',
  },

  // Scroll Container
  scrollContainer: {
    flexGrow: 1,
    padding: 20,
    paddingBottom: 100,
  },

  // Header
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    color: '#AAA',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
    paddingHorizontal: 10,
  },

  // Requirement Notice
  requirementNotice: {
    flexDirection: 'row',
    backgroundColor: '#1a2a3a',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#3498db30',
    gap: 12,
  },
  requirementNoticeContent: {
    flex: 1,
  },
  requirementNoticeTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#3498db',
    marginBottom: 4,
  },
  requirementNoticeText: {
    fontSize: 13,
    color: '#AAA',
    lineHeight: 20,
  },

  // Quick Start Box
  quickStartBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#FCD00030',
  },
  quickStartHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  quickStartTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FCD000',
  },
  checklistItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
    marginLeft: 8,
  },
  checklistText: {
    fontSize: 14,
    color: '#CCC',
  },

  // Steps
  stepContainer: {
    marginBottom: 24,
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FCD000',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
  },
  stepContent: {
    marginLeft: 40,
  },

  // Body Text
  bodyText: {
    fontSize: 14,
    color: '#BBB',
    lineHeight: 22,
    marginBottom: 12,
  },
  highlight: {
    color: '#FCD000',
    fontWeight: '600',
  },
  bold: {
    fontWeight: 'bold',
    color: '#fff',
  },

  // List Items
  listItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  bullet: {
    color: '#FCD000',
    fontSize: 14,
    marginRight: 8,
    width: 12,
  },
  listIcon: {
    marginRight: 8,
    marginTop: 3,
  },
  listItemText: {
    flex: 1,
    fontSize: 14,
    color: '#BBB',
    lineHeight: 20,
  },

  // Required Badge
  requiredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3a1a1a',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 12,
    gap: 8,
  },
  requiredBadgeText: {
    color: '#E74C3C',
    fontSize: 12,
    fontWeight: 'bold',
  },

  // Why Stream Box
  whyStreamBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 14,
    marginVertical: 12,
  },
  whyStreamTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
  },

  // Capture Card Image
  captureCardImageWrapper: {
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 16,
    marginVertical: 12,
  },
  captureCardImage: {
    width: '100%',
    maxWidth: 280,
    height: 180,
  },
  captureCardCaption: {
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },

  // Pool Info Box
  poolInfoBox: {
    backgroundColor: '#1a2a1a',
    borderRadius: 8,
    padding: 14,
    marginVertical: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#2ECC71',
  },
  poolInfoTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2ECC71',
    marginBottom: 8,
  },
  poolInfoText: {
    fontSize: 13,
    color: '#AAA',
    lineHeight: 20,
  },

  // Disclaimer Box
  disclaimerBox: {
    backgroundColor: '#111',
    borderRadius: 8,
    padding: 14,
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#333',
  },
  disclaimerTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#666',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  disclaimerText: {
    fontSize: 11,
    color: '#666',
    lineHeight: 18,
  },

  // Format Cards
  formatCard: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  formatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  formatTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
  },
  formatDesc: {
    fontSize: 13,
    color: '#888',
    lineHeight: 20,
    marginLeft: 26,
  },

  // Collapsible Section
  collapsibleContainer: {
    backgroundColor: '#111',
    borderRadius: 10,
    marginBottom: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#222',
  },
  collapsibleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
  },
  collapsibleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  collapsibleIcon: {
    marginRight: 10,
    width: 24,
    alignItems: 'center',
  },
  collapsibleTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
  },
  collapsibleContent: {
    padding: 14,
    paddingTop: 0,
    borderTopWidth: 1,
    borderTopColor: '#222',
  },

  // Setting Row
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#1a1a1a',
    padding: 10,
    borderRadius: 6,
    marginBottom: 6,
  },
  settingLabel: {
    fontSize: 13,
    color: '#888',
  },
  settingValue: {
    fontSize: 13,
    color: '#FCD000',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },

  // Code Block
  codeBlockContainer: {
    marginBottom: 12,
  },
  codeBlockLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  codeBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 6,
    padding: 12,
  },
  codeBlockText: {
    flex: 1,
    fontSize: 13,
    color: '#fff',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },

  // Tip Box
  tipBox: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 12,
    marginVertical: 12,
    borderLeftWidth: 3,
  },
  tipIcon: {
    marginRight: 10,
  },
  tipText: {
    flex: 1,
    fontSize: 13,
    color: '#CCC',
    lineHeight: 20,
  },

  // Troubleshooting
  troubleshootCategory: {
    fontSize: 12,
    fontWeight: '800',
    color: '#FCD000',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginTop: 14,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  troubleshootItem: {
    marginBottom: 16,
  },
  troubleshootQ: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 6,
  },
  troubleshootA: {
    fontSize: 13,
    color: '#888',
    lineHeight: 22,
    marginLeft: 8,
  },

  // Pool Explainer
  poolExplainer: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  poolHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  poolTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FCD000',
  },
  poolDesc: {
    fontSize: 13,
    color: '#888',
    lineHeight: 20,
    marginLeft: 26,
  },

  // Tier List
  tierList: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    marginVertical: 12,
  },
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  tierBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  tierBadgeText: {
    color: '#000',
    fontSize: 12,
    fontWeight: 'bold',
  },
  tierInfo: {
    fontSize: 13,
    color: '#CCC',
    flex: 1,
  },
  tierMore: {
    fontSize: 13,
    color: '#FCD000',
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Example Card
  exampleCard: {
    backgroundColor: '#1a2a2a',
    borderRadius: 8,
    padding: 14,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#2ECC7130',
  },
  exampleTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  exampleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  exampleRowHighlight: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    paddingTop: 8,
    marginTop: 4,
  },
  exampleLabel: {
    fontSize: 13,
    color: '#888',
  },
  exampleValue: {
    fontSize: 13,
    color: '#fff',
    fontWeight: '600',
  },

  // Grind Card
  grindCard: {
    backgroundColor: '#2a1a2a',
    borderRadius: 8,
    padding: 14,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#9146FF30',
  },
  grindHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  grindTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#9146FF',
  },
  grindRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  grindLabel: {
    fontSize: 13,
    color: '#AAA',
  },
  grindValue: {
    fontSize: 14,
    color: '#2ECC71',
    fontWeight: 'bold',
  },
  grindNote: {
    fontSize: 11,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },
  grindSubtitle: {
    fontSize: 12,
    color: '#9146FF',
    textAlign: 'center',
    marginBottom: 12,
    opacity: 0.8,
  },

  // Extended Example Card Styles
  exampleSubtitle: {
    fontSize: 12,
    color: '#2ECC71',
    textAlign: 'center',
    marginBottom: 10,
    opacity: 0.8,
  },
  exampleDivider: {
    height: 1,
    backgroundColor: '#333',
    marginVertical: 10,
  },
  exampleScenario: {
    fontSize: 13,
    color: '#FCD000',
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 4,
  },
  exampleNote: {
    fontSize: 10,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },

  // Math Explanation Card
  mathCard: {
    backgroundColor: '#1a2a3a',
    borderRadius: 8,
    padding: 14,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#3498db30',
  },
  mathHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 10,
  },
  mathTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#3498db',
  },
  mathText: {
    fontSize: 12,
    color: '#AAA',
    lineHeight: 20,
  },

  // Penalty Table
  penaltyTable: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    marginTop: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#333',
  },
  penaltyTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#222',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  penaltyTableHeaderText: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
    color: '#888',
    textTransform: 'uppercase',
  },
  penaltyTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  penaltyTableBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    alignItems: 'center',
    marginRight: 12,
    alignSelf: 'flex-start',
  },
  penaltyTableBadgeText: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  penaltyTableText: {
    flex: 1,
    fontSize: 13,
    color: '#CCC',
  },

  // Buttons
  actionButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  linkButton: {
    backgroundColor: '#222',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 8,
  },
  linkButtonText: {
    fontSize: 14,
    color: '#3498db',
  },
  backButton: {
    marginTop: 30,
    backgroundColor: '#222',
    paddingVertical: 14,
    paddingHorizontal: 30,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FCD000',
  },
});